import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Car, MapPin, Calendar as CalendarIcon, Clock, Users, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [bookingType, setBookingType] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [route, setRoute] = useState("");
  const [direction, setDirection] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  const vehicles = [
    {
      name: "Saloon",
      image: "https://images.unsplash.com/photo-1664612424080-9738784a1ef3?w=400&q=80",
      description: "Classic luxury sedan, perfect for business travel"
    },
    {
      name: "S Class",
      image: "https://images.unsplash.com/photo-1607332623489-e8ddd788072d?w=400&q=80",
      description: "Executive flagship sedan with ultimate luxury"
    },
    {
      name: "Estate Car",
      image: "https://images.unsplash.com/photo-1664612414212-e6aef1ff8441?w=400&q=80",
      description: "Spacious elegance with extra luggage capacity"
    },
    {
      name: "E Class",
      image: "https://images.unsplash.com/photo-1664612414212-e6aef1ff8441?w=400&q=80",
      description: "Premium business sedan with refined comfort"
    },
    {
      name: "MPV Viano (6 Persons)",
      image: "https://images.unsplash.com/photo-1565340380388-6ecb7426fc63?w=400&q=80",
      description: "Luxury MPV for families and small groups"
    },
    {
      name: "Minibus (8 Persons)",
      image: "https://images.unsplash.com/photo-1698840059740-ba83e510733b?w=400&q=80",
      description: "Spacious transport for larger groups"
    }
  ];

  const routes = [
    "Train Stations",
    "Heathrow T1-4",
    "Heathrow T5",
    "Gatwick",
    "Stansted/Luton",
    "London City"
  ];

  const cityTours = [
    "London Highlights",
    "Royal London",
    "Historic London",
    "London by Night"
  ];

  const ukTours = [
    "Cotswolds",
    "Oxford & Cotswolds",
    "Stonehenge & Bath",
    "Windsor & Eton",
    "Cambridge",
    "Brighton & Seven Sisters",
    "Canterbury & Dover",
    "Stratford-upon-Avon"
  ];

  useEffect(() => {
    if (bookingType && vehicleType) {
      if (bookingType === "airport_transfer" && route && direction) {
        calculatePrice();
      } else if (bookingType.startsWith("as_directed")) {
        calculatePrice();
      }
    }
  }, [bookingType, vehicleType, route, direction]);

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API}/pricing/calculate`, {
        booking_type: bookingType,
        vehicle_type: vehicleType,
        route: bookingType === "airport_transfer" ? route : null,
        direction: bookingType === "airport_transfer" ? direction : null
      });
      setPricing(response.data);
    } catch (error) {
      console.error("Error calculating price:", error);
    }
  };

  const handleSubmit = async () => {
    if (!user && (!guestName || !guestEmail || !guestPhone)) {
      toast.error("Please fill in your contact details");
      return;
    }

    if (!pickupLocation || !dropoffLocation || !date || !time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      const response = await axios.post(`${API}/bookings`, {
        booking_type: bookingType,
        vehicle_type: vehicleType,
        route: bookingType === "airport_transfer" ? route : null,
        direction: bookingType === "airport_transfer" ? direction : null,
        pickup_location: pickupLocation,
        dropoff_location: dropoffLocation,
        date,
        time,
        passengers,
        guest_name: !user ? guestName : null,
        guest_email: !user ? guestEmail : null,
        guest_phone: !user ? guestPhone : null,
        notes
      }, config);

      toast.success("✅ Booking created! Check your email for confirmation.", {
        duration: 4000,
      });
      navigate(`/booking-confirmation/${response.data.id}`);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/elite-cwb-logo.webp" 
                alt="Elite CWB Logo" 
                className="h-24 w-auto" style={{ filter: 'drop-shadow(0 0 10px rgba(212, 175, 55, 0.3))' }}
              />
            </div>
            <div className="flex items-center gap-4">
              {user && (
                <Button 
                  data-testid="dashboard-booking-nav-btn"
                  variant="ghost" 
                  onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}
                >
                  Dashboard
                </Button>
              )}
              <Button 
                data-testid="back-to-home-btn"
                variant="outline" 
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Book Your Journey</h1>
            <p className="text-lg text-gray-600">Secure your booking with just 20% deposit</p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 1 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>
                1
              </div>
              <div className={`h-1 w-16 ${step >= 2 ? 'bg-gray-900' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 2 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>
                2
              </div>
              <div className={`h-1 w-16 ${step >= 3 ? 'bg-gray-900' : 'bg-gray-200'}`}></div>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${step >= 3 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-500'}`}>
                3
              </div>
            </div>
          </div>

          <Card className="shadow-xl border-2">
            <CardContent className="p-8">
              {/* Step 1: Service Type */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Service Type</h2>
                    <p className="text-gray-600">Choose the type of service you need</p>
                  </div>

                  <RadioGroup value={bookingType} onValueChange={setBookingType}>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3 p-4 border-2 rounded-lg hover:border-gray-900 cursor-pointer" onClick={() => setBookingType("airport_transfer")}>
                        <RadioGroupItem data-testid="airport-transfer-radio" value="airport_transfer" id="airport_transfer" />
                        <Label htmlFor="airport_transfer" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-lg">Airport Transfer</div>
                          <div className="text-sm text-gray-600">Transfers to/from London airports</div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 border-2 rounded-lg hover:border-gray-900 cursor-pointer" onClick={() => setBookingType("as_directed_inner")}>
                        <RadioGroupItem data-testid="as-directed-inner-radio" value="as_directed_inner" id="as_directed_inner" />
                        <Label htmlFor="as_directed_inner" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-lg">As Directed - Inner London</div>
                          <div className="text-sm text-gray-600">4-hour minimum service</div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 border-2 rounded-lg hover:border-gray-900 cursor-pointer" onClick={() => setBookingType("as_directed_outer")}>
                        <RadioGroupItem data-testid="as-directed-outer-radio" value="as_directed_outer" id="as_directed_outer" />
                        <Label htmlFor="as_directed_outer" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-lg">As Directed - Outside London</div>
                          <div className="text-sm text-gray-600">6-hour minimum service</div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 border-2 rounded-lg hover:border-gray-900 cursor-pointer" onClick={() => setBookingType("city_tour")}>
                        <RadioGroupItem data-testid="city-tour-radio" value="city_tour" id="city_tour" />
                        <Label htmlFor="city_tour" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-lg">London City Tour</div>
                          <div className="text-sm text-gray-600">Guided tours of London's landmarks</div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-4 border-2 rounded-lg hover:border-gray-900 cursor-pointer" onClick={() => setBookingType("uk_tour")}>
                        <RadioGroupItem data-testid="uk-tour-radio" value="uk_tour" id="uk_tour" />
                        <Label htmlFor="uk_tour" className="flex-1 cursor-pointer">
                          <div className="font-semibold text-lg">UK-Wide Tour</div>
                          <div className="text-sm text-gray-600">Full day tours to Cotswolds, Oxford, Bath & more</div>
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>

                  {bookingType && (
                    <>
                      <div>
                        <Label className="text-lg font-semibold mb-4 block">Select Your Vehicle</Label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {vehicles.map(v => (
                            <div
                              key={v.name}
                              onClick={() => setVehicleType(v.name)}
                              className={`cursor-pointer rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${
                                vehicleType === v.name ? 'border-gray-900 ring-2 ring-gray-900' : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              <div className="aspect-video overflow-hidden">
                                <img 
                                  src={v.image} 
                                  alt={v.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="p-3 bg-white">
                                <h4 className="font-semibold text-gray-900 text-sm">{v.name}</h4>
                                <p className="text-xs text-gray-600 mt-1">{v.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {bookingType === "airport_transfer" && (
                        <>
                          <div>
                            <Label htmlFor="route" className="text-lg font-semibold">Select Route</Label>
                            <Select value={route} onValueChange={setRoute}>
                              <SelectTrigger data-testid="route-select" className="mt-2">
                                <SelectValue placeholder="Choose destination" />
                              </SelectTrigger>
                              <SelectContent>
                                {routes.map(r => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label className="text-lg font-semibold">Direction</Label>
                            <RadioGroup value={direction} onValueChange={setDirection} className="flex gap-4 mt-2">
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem data-testid="inward-direction-radio" value="inward" id="inward" />
                                <Label htmlFor="inward">Inward (To London)</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem data-testid="outward-direction-radio" value="outward" id="outward" />
                                <Label htmlFor="outward">Outward (From London)</Label>
                              </div>
                            </RadioGroup>
                          </div>
                        </>
                      )}

                      {bookingType === "city_tour" && (
                        <div>
                          <Label htmlFor="cityTour" className="text-lg font-semibold">Select City Tour</Label>
                          <Select value={route} onValueChange={setRoute}>
                            <SelectTrigger data-testid="city-tour-select" className="mt-2">
                              <SelectValue placeholder="Choose your tour" />
                            </SelectTrigger>
                            <SelectContent>
                              {cityTours.map(tour => (
                                <SelectItem key={tour} value={tour}>{tour}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {bookingType === "uk_tour" && (
                        <div>
                          <Label htmlFor="ukTour" className="text-lg font-semibold">Select UK Tour</Label>
                          <Select value={route} onValueChange={setRoute}>
                            <SelectTrigger data-testid="uk-tour-select" className="mt-2">
                              <SelectValue placeholder="Choose your destination" />
                            </SelectTrigger>
                            <SelectContent>
                              {ukTours.map(tour => (
                                <SelectItem key={tour} value={tour}>{tour}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {pricing && (
                        <div className="p-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border-2 border-amber-200">
                          <h3 className="text-xl font-bold text-gray-900 mb-3">Price Breakdown</h3>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-700">Total Price:</span>
                              <span className="font-semibold text-lg">£{pricing.total_price}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700">Deposit (20%):</span>
                              <span className="font-semibold text-lg text-amber-700">£{pricing.deposit_amount}</span>
                            </div>
                            <div className="flex justify-between border-t border-amber-300 pt-2">
                              <span className="text-gray-700">Remaining (Pay on day):</span>
                              <span className="font-semibold text-lg">£{pricing.remaining_amount}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <Button 
                    data-testid="step1-next-btn"
                    onClick={() => setStep(2)} 
                    disabled={
                      !bookingType || 
                      !vehicleType || 
                      (bookingType === "airport_transfer" && (!route || !direction)) ||
                      ((bookingType === "city_tour" || bookingType === "uk_tour") && !route)
                    }
                    className="w-full bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
                  >
                    Continue
                  </Button>
                </div>
              )}

              {/* Step 2: Journey Details */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Journey Details</h2>
                    <p className="text-gray-600">Provide your journey information</p>
                  </div>

                  <div>
                    <Label htmlFor="pickup">Pickup Location *</Label>
                    <Input 
                      data-testid="pickup-input"
                      id="pickup" 
                      value={pickupLocation} 
                      onChange={(e) => setPickupLocation(e.target.value)} 
                      placeholder="Enter pickup address"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dropoff">Drop-off Location *</Label>
                    <Input 
                      data-testid="dropoff-input"
                      id="dropoff" 
                      value={dropoffLocation} 
                      onChange={(e) => setDropoffLocation(e.target.value)} 
                      placeholder="Enter drop-off address"
                      className="mt-2"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input 
                        data-testid="date-input"
                        id="date" 
                        type="date" 
                        value={date} 
                        onChange={(e) => setDate(e.target.value)}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="time">Time *</Label>
                      <Input 
                        data-testid="time-input"
                        id="time" 
                        type="time" 
                        value={time} 
                        onChange={(e) => setTime(e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="passengers">Number of Passengers *</Label>
                    <Input 
                      data-testid="passengers-input"
                      id="passengers" 
                      type="number" 
                      min="1" 
                      max="8" 
                      value={passengers} 
                      onChange={(e) => setPassengers(parseInt(e.target.value))}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea 
                      data-testid="notes-textarea"
                      id="notes" 
                      value={notes} 
                      onChange={(e) => setNotes(e.target.value)} 
                      placeholder="Any special requirements or instructions"
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      data-testid="step2-back-btn"
                      onClick={() => setStep(1)} 
                      variant="outline"
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      data-testid="step2-next-btn"
                      onClick={() => setStep(3)} 
                      disabled={!pickupLocation || !dropoffLocation || !date || !time}
                      className="flex-1 bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Contact & Payment */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact & Confirmation</h2>
                    <p className="text-gray-600">Review and complete your booking</p>
                  </div>

                  {!user && (
                    <div className="space-y-4 p-6 bg-gray-50 rounded-lg">
                      <h3 className="font-semibold text-lg">Your Contact Details</h3>
                      <div>
                        <Label htmlFor="guestName">Full Name *</Label>
                        <Input 
                          data-testid="guest-name-input"
                          id="guestName" 
                          value={guestName} 
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Enter your full name"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guestEmail">Email *</Label>
                        <Input 
                          data-testid="guest-email-input"
                          id="guestEmail" 
                          type="email" 
                          value={guestEmail} 
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="guestPhone">Phone Number *</Label>
                        <Input 
                          data-testid="guest-phone-input"
                          id="guestPhone" 
                          value={guestPhone} 
                          onChange={(e) => setGuestPhone(e.target.value)}
                          placeholder="+44 xxx xxx xxxx"
                          className="mt-2"
                        />
                      </div>
                    </div>
                  )}

                  {/* Booking Summary */}
                  <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-lg border-2">
                    <h3 className="font-semibold text-xl mb-4">Booking Summary</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service:</span>
                        <span className="font-semibold">{bookingType === "airport_transfer" ? "Airport Transfer" : bookingType === "as_directed_inner" ? "As Directed (Inner London)" : "As Directed (Outside London)"}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle:</span>
                        <span className="font-semibold">{vehicleType}</span>
                      </div>
                      {bookingType === "airport_transfer" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Route:</span>
                            <span className="font-semibold">{route}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Direction:</span>
                            <span className="font-semibold">{direction === "inward" ? "Inward (To London)" : "Outward (From London)"}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date & Time:</span>
                        <span className="font-semibold">{date} at {time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Passengers:</span>
                        <span className="font-semibold">{passengers}</span>
                      </div>
                      {pricing && (
                        <>
                          <div className="border-t pt-3 mt-3">
                            <div className="flex justify-between text-lg">
                              <span className="text-gray-900 font-semibold">Total Price:</span>
                              <span className="font-bold">£{pricing.total_price}</span>
                            </div>
                            <div className="flex justify-between text-amber-700 mt-2">
                              <span className="font-semibold">Deposit Due (20%):</span>
                              <span className="font-bold text-xl">£{pricing.deposit_amount}</span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      data-testid="step3-back-btn"
                      onClick={() => setStep(2)} 
                      variant="outline"
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button 
                      data-testid="confirm-booking-btn"
                      onClick={handleSubmit} 
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600"
                    >
                      {loading ? "Creating Booking..." : "Confirm Booking"}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Booking;